// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Button from "@tps/MobiusOverrides/Button/Button";
import LazyImage from "@tps/MobiusOverrides/LazyImage/LazyImage";
import { XngCustomGroup } from "@tps/XDL/constants";
import { xScreen } from "@tps/XDL/Utilities";
import parse from "html-react-parser";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    copy = "copy",
    buttonText = "buttonText",
    url = "url",
    imagePath = "imagePath",
    openInNewTab = "openInNewTab",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.copy]: string;
        [fields.buttonText]: string;
        [fields.url]: LinkFieldValue;
        [fields.imagePath]: string;
        [fields.openInNewTab]: boolean;
    };
}

type Props = OwnProps;

export interface InfoCardStyles {
    container?: InjectableCss;
    title?: TypographyProps;
    copy?: TypographyProps;
    buttonContainer?: InjectableCss;
    image?: LazyImageProps;
}

export const infoCardStyles: InfoCardStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            height: 100%;
            border: 1px solid ${getColor("common.ui3")};
        `,
    },
    buttonContainer: {
        css: css`
            margin-top: auto;
            padding: 0 17px 17px 17px;
        `,
    },
    title: {
        size: 20,
        lineHeight: "28px",
        color: "text.main",
        css: css`
            margin-bottom: 12px;
            font-weight: 700;
            padding: 0 17px;
        `,
    },
    copy: {
        size: 14,
        lineHeight: "22px",
        weight: "normal",
        color: "text.main",
        css: css`
            margin-bottom: 20px;
            padding: 0 17px;
        `,
    },
    image: {
        css: css`
            &, img {
                width: 100%;
                height: auto;
                max-height: 100%;
                box-shadow: 0 2px 0 0 ${getColor("primary")};
            }
        `,
    },
};

const styles = infoCardStyles;

const InfoCard = ({ id, fields: { title, copy, buttonText, url, imagePath, openInNewTab } }: Props) => {
    const linkUrl = useGetLink(url);
    const history = useHistory();
    const { isInShell } = useShellContext();

    const handleButtonOnClick = () => {
        if (!linkUrl?.url) {
            return;
        }

        if (openInNewTab) {
            window.open(linkUrl?.url, "_blank");
            return;
        }

        if (linkUrl?.url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.open(linkUrl?.url);
        } else {
            history.push(linkUrl?.url);
        }
    };

    return (
        <StyledWrapper className="info-card" {...styles.container}>
            {imagePath && (
                <StyledWrapper
                    css={css`
                        width: 100%;
                        height: 132px;
                        margin-bottom: 16px;

                        ${xScreen("lte", "md")} {
                            height: 110px;
                        }

                        ${xScreen("lte", "xs")} {
                            height: 101px;
                        }
                    `}
                >
                    <LazyImage {...styles.image} src={imagePath} altText={`${title}_card_image`}></LazyImage>
                </StyledWrapper>
            )}
            <Typography {...styles.title}>{title}</Typography>
            <Typography {...styles.copy}>{parse(copy, parserOptions)}</Typography>
            {buttonText && (
                <StyledWrapper {...styles.buttonContainer}>
                    <Button
                        variant="primary"
                        onClick={handleButtonOnClick}
                    >
                        {buttonText}
                    </Button>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: InfoCard,
    definition: {
        displayName: "Info Card",
        group: XngCustomGroup,
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.imagePath,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.copy,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                extendedConfig: { height: 120 },
            },
            {
                name: fields.buttonText,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.url,
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.openInNewTab,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
